import type { Stage } from "@amzn/ads-forge-client";
import {
    fetchAndStoreForgeApiCredentials,
    useForgeNarrativeDownload,
} from "@amzn/ads-forge-web-assets";
import type { TemplateMetadata } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { isUndefined } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useStore } from "zustand";

import { COPY_MESSAGES, TOOLTIP_MESSAGES } from "../../../../constants/common";
import { G4sPlacement } from "../../../../constants/g4sPlacement";
import { TELEMETRY_ID } from "../../../../constants/telemetry";
import { useSetInsightDetailComponents } from "../../../../hooks/insightDetail/useSetInsightDetailComponents";
import { authStore } from "../../../../store/auth";
import { FeatureFlagsStore } from "../../../../store/featureFlags";
import { insightDetailComponentStore } from "../../../../store/insightDetailComponent";
import { InsightLinkageStore } from "../../../../store/insightLinkage";
import { workbenchStore } from "../../../../store/workbench";
import type { CardDetail } from "../../../../types/card";
import type { DownloadAsset } from "../../../../types/common";
import { InsightPreviewFileFormats } from "../../../../types/insightPreviewFileFormats";
import {
    generateSharableLinkId,
    generateShareableUrl,
} from "../../../../utils/api.utils";
import { copyToClipboard } from "../../../../utils/clipboard.utils";
import type {
    InsightDetailOption,
    InsightDetailSelectionProps,
} from "../../../../utils/insightDetail.utils";
import { InsightDetailType } from "../../../../utils/insightDetail.utils";
import {
    getFilterTelemetryDetails,
    getUnifiedFilterTelemetryDetails,
    logWithTakt,
} from "../../../../utils/telemetry.utils";
import { PdfSelectionViewer } from "../../../common/PdfSelectionViewer";
import { ShareIcon } from "../../../common/ShareIcon";
import {
    BodyComponentBuilder,
    FooterComponentBuilder,
    FooterDownloadButton,
    HeaderCardDetails,
    HeaderComponentBuilder,
    Header,
    HeaderTitle,
    IcmBusinesslineConfiguration,
} from "../../../common/insightDetail.components";
import { DeckDecorationWithLanguageModalButton } from "../../../createDeckDecoration/DeckDecorationWithLanguageModalButton";
import { WorkbenchActions } from "../insightDetails/WorkbenchActions";

export interface InsightFamilyDetails {
    details: CardDetail[];
    title: string;
    identifier: string;
    familyName: string;
    insightFamilyTrackingId: string;
    insightType?: InsightType;
    templateInfo?: TemplateMetadata;
    previewAsset?: DownloadAsset;
    downloadAsset?: DownloadAsset;
    businesslineId?: string;
    icmVersion?: string;
    previewLoading: boolean;
    downloadLoading: boolean;
    reset: () => void;
}

export const useSetInsightFamilyDetails = (
    props: InsightFamilyDetails,
): (() => void) => {
    const {
        identifier,
        familyName,
        details,
        insightType,
        title,
        templateInfo,
        previewAsset,
        downloadAsset,
        previewLoading,
        downloadLoading,
        insightFamilyTrackingId,
        businesslineId,
        icmVersion,
        reset,
    } = props;

    const [resetStore] = useStore(insightDetailComponentStore, (state) => [
        state.reset,
    ]);
    const settings = useStore(authStore, (state) => state.settings);
    const username = useStore(authStore, (state) => state.username);
    const [displayComponents, setDisplayComponents] = useState(false);

    const [useUnifiedSearch] = useStore(FeatureFlagsStore, (state) => [
        state.unifiedSearch,
    ]);

    const [getSlideIndexes] = useStore(workbenchStore, (state) => [
        state.getSlideIndexes,
    ]);

    const [setInsightLinkageOpen, setInsightLinkageMetadata] = useStore(
        InsightLinkageStore,
        (state) => [state.setOpen, state.setInsightLinkageMetadata],
    );

    const [numPages, setNumPages] = React.useState<number>(0);
    const { downloadNarrative } = useForgeNarrativeDownload();

    useEffect(() => {
        const fetchCredentials = async () => {
            await fetchAndStoreForgeApiCredentials({
                stage: settings.stage as Stage,
                username,
                cognitoPoolId: settings.cognitoPoolId,
                accountId: settings.blinkAwsAccountId,
            });
        };
        void fetchCredentials();
    }, []);

    const handleMount = (props: InsightDetailSelectionProps): void => {
        props.setSelectedSlides(getSlideIndexes(identifier));
    };

    const handleClose = () => {
        setNumPages(0);
        resetStore();
        reset();
    };

    const handleDownload = () => {
        setInsightLinkageMetadata({
            title,
            content: downloadAsset?.content,
            identifier: insightFamilyTrackingId,
            downloadUrl: downloadAsset?.downloadUrl,
            insightType: InsightType.INSIGHT,
            description: "",
        });
        setInsightLinkageOpen(true);
        handleClose();
    };

    const InsightFamilyHeaderOption: InsightDetailOption = useMemo(() => {
        const handleShareClick = async () => {
            const baseUrl = window.location.origin + window.location.pathname;

            if (!identifier) {
                return;
            }

            const sharableIdentifier = await generateSharableLinkId(
                identifier,
                insightType,
            );

            const urlParams: Record<string, string> = {
                identifier: sharableIdentifier,
            };

            const searchParams = new URLSearchParams(window.location.search);
            const currentFilter = searchParams.get("filters");
            const sfId = searchParams.get("sfId");

            if (sfId) {
                urlParams.sfId = sfId;
            }

            if (currentFilter) {
                urlParams.filters = currentFilter;
            }

            const shareUrl = generateShareableUrl(baseUrl, urlParams);
            copyToClipboard(shareUrl, "share-url", COPY_MESSAGES.INSIGHT);
            const filters = useUnifiedSearch
                ? getUnifiedFilterTelemetryDetails()
                : getFilterTelemetryDetails();

            logWithTakt(TELEMETRY_ID.INSIGHT_SHARE_SUCCESS, {
                ...getFilterTelemetryDetails(),
                identifier,
                shareType: "insightFamily",
            });
        };

        const uxTreatmentsEnabled =
            FeatureFlagsStore.getState().uxTreatmentsOnSidePanel;

        if (uxTreatmentsEnabled) {
            const InsightFamilyHeader: React.FunctionComponent<
                InsightDetailSelectionProps
            > = (props: InsightDetailSelectionProps) => (
                <HeaderComponentBuilder
                    loading={previewLoading}
                    headerComponents={[
                        <Header
                            numPages={numPages}
                            identifier={identifier}
                            insightType={insightType}
                        >
                            <ShareIcon
                                onClick={() => void handleShareClick()}
                                tooltipMessage={TOOLTIP_MESSAGES.INSIGHT}
                                testId="insight-family-share-icon"
                            />
                        </Header>,
                        <HeaderTitle title={title} />,
                        <HeaderCardDetails
                            details={details}
                            templateInfo={templateInfo}
                            selectedSlides={props.selectedSlides}
                            setSelectedSlides={props.setSelectedSlides}
                            numPages={numPages}
                            previewAsset={previewAsset}
                            identifier={identifier}
                            insightType={InsightType.INSIGHT}
                            previewFileFormat={InsightPreviewFileFormats.PDF}
                            downloadAsset={downloadAsset}
                            isCompliant={true}
                            outputFileFormats={[]}
                        />,
                        businesslineId && icmVersion ? (
                            <IcmBusinesslineConfiguration
                                businesslineId={businesslineId}
                                icmVersion={icmVersion}
                            />
                        ) : (
                            <></>
                        ),
                    ]}
                />
            );
            return {
                type: InsightDetailType.SELECTION,
                component: InsightFamilyHeader,
            };
        } else {
            const InsightFamilyHeader: React.FunctionComponent = () => (
                <HeaderComponentBuilder
                    loading={previewLoading}
                    headerComponents={[
                        <Header
                            numPages={numPages}
                            identifier={identifier}
                            insightType={insightType}
                        >
                            <ShareIcon
                                onClick={() => void handleShareClick()}
                                tooltipMessage={TOOLTIP_MESSAGES.INSIGHT}
                                testId="insight-family-share-icon"
                            />
                        </Header>,
                        <HeaderTitle title={title} />,
                        <HeaderCardDetails
                            details={details}
                            templateInfo={templateInfo}
                        />,
                        businesslineId && icmVersion ? (
                            <IcmBusinesslineConfiguration
                                businesslineId={businesslineId}
                                icmVersion={icmVersion}
                            />
                        ) : (
                            <></>
                        ),
                    ]}
                />
            );
            return {
                type: InsightDetailType.VIEW,
                component: InsightFamilyHeader,
            };
        }
    }, [
        identifier,
        numPages,
        details,
        templateInfo,
        previewAsset,
        previewLoading,
    ]);

    const InsightFamilyBodyOption: InsightDetailOption = useMemo(() => {
        const InsightFamilyBody: React.FunctionComponent<
            InsightDetailSelectionProps
        > = (props: InsightDetailSelectionProps) => (
            <BodyComponentBuilder
                loading={previewLoading}
                bodyComponents={[
                    <PdfSelectionViewer
                        numPages={numPages}
                        setNumPages={setNumPages}
                        url={previewAsset ? previewAsset.downloadUrl! : ""}
                        insightType={InsightType.INSIGHT}
                        identifier={identifier}
                        selectedSlides={props.selectedSlides}
                        setSelectedSlides={props.setSelectedSlides}
                        isCompliant={true}
                    />,
                ]}
            />
        );
        return {
            type: InsightDetailType.SELECTION,
            component: InsightFamilyBody,
        };
    }, [previewAsset, numPages, identifier, previewLoading]);

    const InsightFamilyFooterOption: InsightDetailOption = useMemo(() => {
        const InsightFamilyFooter: React.FunctionComponent<
            InsightDetailSelectionProps
        > = (props: InsightDetailSelectionProps) => (
            <FooterComponentBuilder
                loading={downloadLoading}
                handleClose={handleClose}
                identifier={identifier}
                insightType={insightType}
                footerComponents={[
                    !FeatureFlagsStore.getState().uxTreatmentsOnSidePanel ? (
                        <DeckDecorationWithLanguageModalButton
                            documentUrl={previewAsset?.downloadUrl}
                            identifier={identifier}
                            insightType={InsightType.INSIGHT}
                            placement={G4sPlacement.SIDE_PANEL}
                        />
                    ) : (
                        <></>
                    ),
                    <FooterDownloadButton
                        handleDownload={handleDownload}
                        downloadAsset={downloadAsset}
                        identifier={identifier}
                        insightType={insightType}
                    />,
                    <WorkbenchActions
                        title={title}
                        identifier={insightFamilyTrackingId}
                        selectedSlides={props.selectedSlides}
                        setSelectedSlides={props.setSelectedSlides}
                        outputFileFormats={[]}
                        numPages={numPages}
                        downloadAsset={downloadAsset}
                        previewAsset={previewAsset}
                        insightType={InsightType.INSIGHT}
                        insightTypeSubType={familyName}
                        isCompliant={true}
                    />,
                ]}
            />
        );
        return {
            type: InsightDetailType.SELECTION,
            component: InsightFamilyFooter,
        };
    }, [
        insightFamilyTrackingId,
        downloadAsset,
        previewAsset,
        numPages,
        downloadLoading,
    ]);

    const previewReady: boolean = useMemo(() => {
        return !isUndefined(previewAsset) || previewLoading;
    }, [previewAsset, previewLoading]);

    const downloadReady: boolean = useMemo(() => {
        return !isUndefined(downloadAsset) || downloadLoading;
    }, [downloadAsset, downloadLoading]);

    const onClick = useSetInsightDetailComponents({
        identifier,
        familyName,
        InsightHeaderOption: InsightFamilyHeaderOption,
        InsightBodyOption: InsightFamilyBodyOption,
        InsightFooterOption: InsightFamilyFooterOption,
        headerReady: displayComponents && previewReady,
        bodyReady: displayComponents && previewReady,
        footerReady: displayComponents && downloadReady,
        handleMount,
        handleClose,
    });

    const handleSetDisplayComponents = () => {
        setDisplayComponents(true);
        onClick();
    };

    return handleSetDisplayComponents;
};
